import {
  LOYALTY_ENTRY_KINDS,
  LOYALTY_MEMBER_SORTS,
  type LoyaltyEntryKind,
  type LoyaltyMemberSort,
} from '@sahab/contracts'
import { z } from 'zod'
import { endOfMonth, isIsoDate, startOfMonth, todayIso } from '../../lib/dates'

export const PAGE_SIZES = [25, 50, 100] as const

const pageSize = z
  .number()
  .refine((n): n is (typeof PAGE_SIZES)[number] => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)

const page = z.number().int().min(1).optional().catch(undefined)

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

// A hand-written `?q=99112045` arrives as a number (the router parses JSON): keep it as text.
const q = z.coerce.string().trim().max(200).optional().catch(undefined)

// ── members ──────────────────────────────────────────────────────────────────

export type MemberSort = LoyaltyMemberSort | `-${LoyaltyMemberSort}`

export function isMemberSort(value: string): value is MemberSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (LOYALTY_MEMBER_SORTS as readonly string[]).includes(field)
}

export const MEMBER_BALANCES = ['positive', 'owing', 'zero'] as const
export type MemberBalance = (typeof MEMBER_BALANCES)[number]

/** The members list lives in the URL; an invalid value falls back to the default. */
export const memberSearchSchema = z.object({
  page,
  pageSize,
  sort: z.string().refine(isMemberSort).optional().catch(undefined),
  q,
  status: z.enum(['ACTIVE', 'BLOCKED']).optional().catch(undefined),
  balance: z.enum(MEMBER_BALANCES).optional().catch(undefined),
  tierId: z.uuid().optional().catch(undefined),
})
export type MemberSearch = z.infer<typeof memberSearchSchema>

export interface MemberListParams {
  page: number
  pageSize: number
  sort: string
  q: string | undefined
  status: 'ACTIVE' | 'BLOCKED' | undefined
  balance: MemberBalance | undefined
  tierId: string | undefined
}

/** Alphabetical by default (the API's default too). */
export const MEMBER_DEFAULT_SORT = 'name'

export function toMemberListQuery(search: MemberSearch): MemberListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? MEMBER_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.status,
    balance: search.balance,
    tierId: search.tierId,
  }
}

// ── the ledger ───────────────────────────────────────────────────────────────

/** The company-wide transactions list: kind, period and search in the URL. */
export const entrySearchSchema = z.object({
  page,
  pageSize,
  q,
  kind: z.enum(LOYALTY_ENTRY_KINDS).optional().catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type EntrySearch = z.infer<typeof entrySearchSchema>

export interface EntryListParams {
  page: number
  pageSize: number
  memberId?: string | undefined
  q?: string | undefined
  kind?: LoyaltyEntryKind | undefined
  from?: string | undefined
  to?: string | undefined
}

export function toEntryListQuery(search: EntrySearch): EntryListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    q: search.q || undefined,
    kind: search.kind,
    from: search.from,
    to: search.to,
  }
}

/** A member's page: its statement's page and kind filter. */
export const memberPageSearchSchema = z.object({
  page,
  kind: z.enum(LOYALTY_ENTRY_KINDS).optional().catch(undefined),
})
export type MemberPageSearch = z.infer<typeof memberPageSearchSchema>

// ── overview ─────────────────────────────────────────────────────────────────

/** The overview's period (default: this month, never written to the URL). */
export const overviewSearchSchema = z.object({ from: isoDate, to: isoDate })
export type OverviewSearch = z.infer<typeof overviewSearchSchema>

export function overviewPeriod(
  search: OverviewSearch,
  today: string = todayIso(),
): { from: string; to: string } {
  const from = search.from ?? startOfMonth(today)
  const to = search.to ?? endOfMonth(today)
  return from <= to ? { from, to } : { from: to, to: from }
}
